import { useState } from 'react';
import * as R from 'ramda';

import { Button, Switch } from '@atom/mui';
import colors from '@atom/styles/colors';
import fontStyles from '@atom/styles/fonts';
import { AttributeGroupItem } from '@atom/types/form';
import {
  AttributesType,
  HydratedAttributeGroupsItem,
} from '@atom/types/inventory';

import AttributeBlock from './AttributeBlock';

import classes from '../formBuilder.module.css';

const styles = {
  toggleLabelStyles: {
    fontSize: fontStyles.md,
    color: colors.neutral.dark,
    whiteSpace: 'nowrap',
    paddingLeft: '0.5rem',
  },
  buttonStyles: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    alignContent: 'center',
  },
  labelStyles: {
    color: colors.brand.blue,
    whiteSpace: 'nowrap',
    paddingRight: '1rem',
  },
};

interface Props {
  fullAttributeGroup: HydratedAttributeGroupsItem;
  assetAttributeGroup: HydratedAttributeGroupsItem;
  schemaId: string;
  fieldId: string;
  updateAttributeGroup: (
    fieldId: string,
    schemaId: string,
    addGroup: boolean,
    attributeGroup: AttributeGroupItem,
  ) => void;
  updateAttribute: (
    fieldId: string,
    schemaId: string,
    addAttribute: boolean,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => void;
  addAllAttributes: (
    fieldId: string,
    schemaId: string,
    attributeGroupName: string,
    attributes: AttributesType[],
  ) => void;
}

const AttributeGroupBlock = ({
  assetAttributeGroup,
  updateAttributeGroup,
  updateAttribute,
  fieldId,
  schemaId,
  fullAttributeGroup,
  addAllAttributes,
}: Props) => {
  const [toggled, setToggled] = useState<boolean>(
    fullAttributeGroup.name === assetAttributeGroup.name,
  );
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const toggleGroup = () => {
    const addGroup = !toggled;
    const attributeGroup = { name: fullAttributeGroup.name, attributes: [] };

    updateAttributeGroup(fieldId, schemaId, addGroup, attributeGroup);

    setToggled(!toggled);
  };

  const handleSelectAll = () => {
    addAllAttributes(
      fieldId,
      schemaId,
      fullAttributeGroup.name,
      fullAttributeGroup.attributes,
    );

    setSelectAll(true);
  };

  const isAttributeChecked = (id: string): boolean => {
    if (R.isNil(assetAttributeGroup.attributes)) {
      return false;
    }

    return !!assetAttributeGroup.attributes.find(
      (attribute: AttributesType): boolean => attribute.id === id,
    );
  };

  return (
    <div className={classes.attributeGroupBlockContainer}>
      <div className={classes.attributeGroupHeader}>
        <Switch
          sx={styles.toggleLabelStyles}
          label={fullAttributeGroup.name}
          name={fullAttributeGroup.name}
          checked={toggled}
          onChange={toggleGroup}
        />
        {toggled && (
          <Button color="primary" onClick={handleSelectAll}>
            SELECT ALL
          </Button>
        )}
      </div>
      <div className={classes.attributeListContainer}>
        {toggled &&
          fullAttributeGroup.attributes.map((fullAttribute: AttributesType) => {
            return (
              <AttributeBlock
                fieldId={fieldId}
                schemaId={schemaId}
                attributeGroupName={fullAttributeGroup.name}
                key={fullAttribute.name}
                fullAttribute={fullAttribute}
                isAttributeChecked={isAttributeChecked(fullAttribute.id)}
                selectAll={selectAll}
                updateAttribute={updateAttribute}
              />
            );
          })}
      </div>
    </div>
  );
};

export default AttributeGroupBlock;
